import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, ExternalLink, Phone } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import { retryAfterSeconds, useCountdown } from '@/lib/useCountdown';
import {
  ChoiceRow,
  FormError,
  GhostButton,
  Hint,
  PrimaryButton,
  TextField,
} from '@/components/form/primitives';
import { MY_ORGANIZATIONS_KEY, useVerifyAttachMutation, useVerifyConfirmMutation } from './hooks';
import styles from './VerificationFlow.module.css';

// Флоу верификации организации — шаги PostFlow прототипа («Привязка
// организации» → код), расширенные выбором канала (SMS/email — решение
// 2026-09-01 дизайн-дока §3) и состоянием «карточки 2ГИС нет» (накопитель №1).
//
// Официальная справка 2ГИС — статья «Как поделиться ссылкой на карточку»
// (URL дал пользователь на приёмке 2026-09-05; до этого вели на корень
// справочного центра — глубокую ссылку было не подтвердить, см. накопитель №1).
// Рядом — свой короткий текст про бесплатное добавление и модерацию.
const GIS_HELP_URL = 'https://help.2gis.ru/question/kak-podelitsya-ssylkoy-na-kartochku-v-2gis';

type Step = 'gis' | 'channel' | 'code' | 'done';
type Channel = 'phone' | 'email';

export function VerificationFlow({ organizationId }: { organizationId: string }): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('gis');
  const [gisUrl, setGisUrl] = useState('');
  // Email — приоритетный канал (SMS платный; доводка приёмки V1).
  const [channel, setChannel] = useState<Channel>('email');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const attachMutation = useVerifyAttachMutation(organizationId);
  const confirmMutation = useVerifyConfirmMutation(organizationId);
  const pending = attachMutation.isPending || confirmMutation.isPending;
  // №20-в: таймер повторной отправки кода (сервер держит интервал 60 с).
  const [resendUntil, setResendUntil] = useState<number | null>(null);
  const resendIn = useCountdown(resendUntil);

  const submitAttach = (): void => {
    setError(null);
    attachMutation.mutate(
      { gis_url: gisUrl.trim(), channel, destination: destination.trim() },
      {
        onSuccess: () => {
          setCode('');
          setStep('code');
          setResendUntil(Date.now() + 60_000);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'resend_too_soon') {
              setResendUntil(Date.now() + retryAfterSeconds(err.details) * 1000);
              setStep('code');
              return setError(t('org.verify.errors.tooSoon'));
            }
            if (err.code === 'rate_limited') return setError(t('org.verify.errors.rateLimited'));
            if (err.code === 'invalid_phone') return setError(t('org.verify.errors.invalidPhone'));
            if (err.code === 'invalid_email') return setError(t('org.verify.errors.invalidEmail'));
            if (err.status === 422) return setError(t('org.verify.errors.gisUrl'));
            if (err.status === 409) return setError(t('org.verify.errors.alreadyVerified'));
          }
          setError(t('org.verify.errors.generic'));
        },
      },
    );
  };

  const submitConfirm = (): void => {
    setError(null);
    confirmMutation.mutate(
      { code: code.trim() },
      {
        onSuccess: () => setStep('done'),
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 400) return setError(t('org.verify.errors.invalidCode'));
            if (err.status === 409) return setError(t('org.verify.errors.alreadyVerified'));
          }
          setError(t('org.verify.errors.generic'));
        },
      },
    );
  };

  const stepTitle =
    step === 'gis'
      ? t('org.verify.stepGis')
      : step === 'channel'
        ? t('org.verify.stepChannel')
        : step === 'code'
          ? t('org.verify.stepCode')
          : t('org.verify.success');

  return (
    <section className={styles.card} aria-label={t('org.verify.stepGis')}>
      <h3 className={styles.stepTitle}>{stepTitle}</h3>

      <FormError>{error}</FormError>

      {step === 'gis' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setStep('channel');
          }}
        >
          <Hint>{t('org.verify.gisHint')}</Hint>
          <TextField
            label={t('org.verify.gisLabel')}
            value={gisUrl}
            onChange={setGisUrl}
            type="url"
            inputMode="url"
            name="gis_url"
            placeholder={t('org.verify.gisPlaceholder')}
          />
          <div className={styles.noCard}>
            <h4 className={styles.noCardTitle}>{t('org.verify.noCard.title')}</h4>
            <p className={styles.noCardText}>{t('org.verify.noCard.text')}</p>
            <a
              className={styles.noCardLink}
              href={GIS_HELP_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              <ExternalLink size={12} aria-hidden="true" />
              {t('org.verify.noCard.link')}
            </a>
          </div>
          <PrimaryButton disabled={!gisUrl.trim()}>{t('org.verify.continue')}</PrimaryButton>
        </form>
      )}

      {step === 'channel' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitAttach();
          }}
        >
          <Hint>{t('org.verify.channelHint')}</Hint>
          <ChoiceRow<Channel>
            value={channel}
            onChange={(next) => {
              setChannel(next);
              setDestination('');
              setError(null);
            }}
            label={t('org.verify.channelLabel')}
            options={[
              { value: 'email', label: t('org.verify.channelEmail') },
              { value: 'phone', label: t('org.verify.channelSms') },
            ]}
          />
          {channel === 'phone' ? (
            <TextField
              label={t('org.verify.phoneLabel')}
              value={destination}
              onChange={setDestination}
              type="tel"
              inputMode="tel"
              name="destination"
              placeholder={t('org.verify.phonePlaceholder')}
            />
          ) : (
            <TextField
              label={t('org.verify.emailLabel')}
              value={destination}
              onChange={setDestination}
              type="email"
              inputMode="email"
              name="destination"
              placeholder={t('org.verify.emailPlaceholder')}
            />
          )}
          <PrimaryButton disabled={pending || !destination.trim()}>
            {t('org.verify.sendCode')}
          </PrimaryButton>
          <div className={styles.secondaryRow}>
            <GhostButton disabled={pending} onClick={() => setStep('gis')}>
              {t('org.verify.changeData')}
            </GhostButton>
          </div>
        </form>
      )}

      {step === 'code' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitConfirm();
          }}
        >
          <p className={styles.codeSent}>
            <Phone size={13} aria-hidden="true" />
            {t('org.verify.codeSent', { destination: destination.trim() })}
          </p>
          <TextField
            label={t('org.verify.codeLabel')}
            value={code}
            onChange={setCode}
            inputMode="numeric"
            name="code"
            autoComplete="one-time-code"
            placeholder="••••••"
            maxLength={6}
          />
          <PrimaryButton disabled={pending || !code.trim()}>
            {t('org.verify.confirm')}
          </PrimaryButton>
          <div className={styles.secondaryRow}>
            <GhostButton disabled={pending} onClick={() => setStep('gis')}>
              {t('org.verify.changeData')}
            </GhostButton>
            <GhostButton disabled={pending || resendIn > 0} onClick={submitAttach}>
              {resendIn > 0
                ? t('org.verify.resendIn', { seconds: resendIn })
                : t('org.verify.resend')}
            </GhostButton>
          </div>
        </form>
      )}

      {step === 'done' && (
        <div className={styles.success}>
          <div className={styles.successIcon}>
            <Check size={24} aria-hidden="true" />
          </div>
          <p className={styles.successText}>{t('org.verify.success')}</p>
          <p className={styles.successHint}>{t('org.verify.successHint')}</p>
          {/* Рефетч списка → карточка выше становится ✓, флоу размонтируется.
              (Инвалидация сознательно НЕ в мутации confirm — см. hooks.ts.) */}
          <PrimaryButton
            type="button"
            onClick={() => void qc.invalidateQueries({ queryKey: MY_ORGANIZATIONS_KEY })}
          >
            {t('org.verify.done')}
          </PrimaryButton>
        </div>
      )}
    </section>
  );
}
